import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Link, useNavigate } from "react-router-dom";
import { Search, FileCheck, ArrowRight, BarChart3, Lock, Globe, AlertTriangle, UserCog, Gavel, Clock, ShieldCheck } from "lucide-react";
import AuditLoading from "@/components/AuditLoading";
import Seo from "@/components/Seo";
import { SwissFlag } from "@/components/SwissFlag";
import type { AuditReport, AuditReportIndexEntry } from "@/types/audit";
import { buildAuditReport } from "@/lib/audit-response";
import { normalizeUrl } from "@/lib/url";
import PasteModal, { type PasteValues } from "@/components/PasteModal";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type FlowState = "form" | "paste-modal" | "loading";

// Recent local reports are reused for 7 days for the same URL — mirrors the
// backend's 7-day report cache so a re-submit of the same site doesn't fire a
// redundant scan and, crucially, gives the user immediate visible feedback
// instead of a silent no-op.
const RECENT_REPORT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

// Returns the most recent locally-persisted report entry for `normalizedUrl`
// if one exists and is younger than RECENT_REPORT_MAX_AGE_MS, else null.
const findRecentLocalReport = (
  normalizedUrl: string,
): AuditReportIndexEntry | null => {
  try {
    const idxRaw = localStorage.getItem("nlpd-reports-index");
    if (!idxRaw) return null;
    const idx: AuditReportIndexEntry[] = JSON.parse(idxRaw);
    const target = normalizedUrl.replace(/\/+$/, "").toLowerCase();
    const match = idx
      .filter(
        (e) =>
          typeof e.url === "string" &&
          e.url.replace(/\/+$/, "").toLowerCase() === target &&
          typeof e.createdAt === "number" &&
          Date.now() - e.createdAt < RECENT_REPORT_MAX_AGE_MS &&
          // The report payload must still be in localStorage to be servable.
          localStorage.getItem(`nlpd-report-${e.reportId}`) !== null,
      )
      .sort((a, b) => b.createdAt - a.createdAt)[0];
    if (!match) return null;
    // Don't re-serve a genuinely FAILED scan as "a recent diagnostic
    // exists": a DNS/crawl failure (e.g. "Domaine non résolu") must let the
    // user re-run, not show the stale failure again. policy_not_found
    // (valid completed-but-limited result, PR #309), unsupported_language
    // (stable site fact) and rate_limited (re-running just re-hits the
    // limiter) stay intentionally servable.
    try {
      const raw = localStorage.getItem(`nlpd-report-${match.reportId}`);
      const rep = raw ? (JSON.parse(raw) as AuditReport) : null;
      const r = rep?.scanFailureReason;
      const GENUINE_FAILURE = new Set([
        "cookie_wall", "ssl_failure", "timeout", "http_404", "anti_bot",
        "client_side_render", "budget_exceeded", "waf_blocked",
        "fetch_blocked", "unknown",
      ]);
      if (r && GENUINE_FAILURE.has(r)) return null;
    } catch {
      // Unparseable stored report → leave it servable (existing behaviour).
    }
    return match;
  } catch {
    return null;
  }
};

const persistReport = (report: AuditReport) => {
  try {
    localStorage.setItem(`nlpd-report-${report.reportId}`, JSON.stringify(report));
    const idxRaw = localStorage.getItem("nlpd-reports-index");
    const idx: AuditReportIndexEntry[] = idxRaw ? JSON.parse(idxRaw) : [];
    const filtered = idx.filter((e) => e.reportId !== report.reportId);
    const next: AuditReportIndexEntry[] = [
      {
        reportId: report.reportId,
        url: report.url,
        // Phase A.2 (Mistral migration): score/scoreLabel are no longer
        // emitted; keep the fields in the index entry shape for back-compat
        // (old localStorage entries had them) but populate from the report
        // if the row predates the migration.
        score: report.score ?? null,
        scoreLabel: report.scoreLabel ?? null,
        createdAt: Date.now(),
      },
      ...filtered,
    ].slice(0, 20);
    localStorage.setItem("nlpd-reports-index", JSON.stringify(next));
  } catch {
    // quota or other localStorage error — silently ignore
  }
};

const Index = () => {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [flowState, setFlowState] = useState<FlowState>("form");
  // Whether the in-flight analysis includes a pasted policy — drives which
  // stage list AuditLoading shows (surface-only scans have no paste stage).
  const [loadingHasPaste, setLoadingHasPaste] = useState(true);
  const [analyzedUrl, setAnalyzedUrl] = useState("");
  // pendingUrl holds the validated URL while the paste modal is open
  const [pendingUrl, setPendingUrl] = useState("");
  // Admins always get a fresh scan: skip the localStorage "recent report"
  // short-circuit so the request reaches the backend (where the decoupled
  // admin cache-bypass then skips the 7-day result cache too). Mirrors the
  // ProtectedRoute getSession + is_admin RPC pattern. Defaults false; any
  // failure leaves it false (normal caching behaviour).
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      const urlParam = params.get("url");
      if (urlParam) setUrl(urlParam);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (cancelled || !session?.user) return;
        return supabase
          .rpc("is_admin", { uid: session.user.id })
          .then(({ data }) => {
            if (!cancelled) setIsAdmin(data === true);
          });
      })
      .catch(() => {
        // Non-fatal: any auth/RPC rejection → stay non-admin (normal
        // caching). Never surfaces as an unhandled rejection.
      });
    return () => {
      cancelled = true;
    };
  }, []);


  const runAnalysis = async (
    target: string,
    pastes?: PasteValues,
  ) => {
    setLoadingHasPaste(!!(pastes && pastes.privacy.length >= 200));
    setFlowState("loading");

    const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
    const POLL_INTERVAL_MS = 5_000;
    const MAX_WAIT_MS = 5 * 60 * 1000; // 5 minutes — covers slow scans

    // The free scan is served entirely by the Supabase `free-scan` edge
    // function. (The legacy n8n webhook flow was retired — the percentage
    // cutover ramp is complete, so there is no longer a routing choice.)
    // Submit returns 202 {reportId, statusUrl}; the rest of the flow polls
    // statusUrl until the report is ready.
    const submitUrl = `${FUNCTIONS_BASE}/free-scan`;

    // Build submit body — sends the `pastes` object (privacy/cookies/impressum).
    const submitBody: Record<string, unknown> = { url: target };
    if (pastes && pastes.privacy.length >= 200) {
      // Cap at 200_000 chars to match the backend's sanitizePaste hard cap and
      // the modal's maxLength. Long-form privacy policies (Migros, banks, big-4
      // consultancies) routinely exceed 60K; 200K covers the long tail.
      const pastesObj: Record<string, string> = {};
      if (pastes.privacy) pastesObj.privacy = pastes.privacy.substring(0, 200_000);
      if (pastes.cookies) pastesObj.cookies = pastes.cookies.substring(0, 200_000);
      if (pastes.impressum) pastesObj.impressum = pastes.impressum.substring(0, 200_000);
      submitBody.pastes = pastesObj;
    }

    try {
      // Step 1: submit the audit job. Backend responds in ~250ms with HTTP 202
      // and {reportId, status: "processing", statusUrl, reportUrl}.
      const submitHeaders: Record<string, string> = {
        "Content-Type": "application/json",
      };
      {
        const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as
          | string
          | undefined;
        if (publishableKey) {
          // apikey is always the anon/publishable key (Supabase gateway requires it).
          submitHeaders.apikey = publishableKey;
          // Authorization: send the logged-in user's access token when a session
          // exists — the edge function reads it to detect admin callers and bypass
          // the 7-day report cache for them. Falls back to the publishable key for
          // anonymous users.
          const { data: { session: authSession } } = await supabase.auth.getSession();
          submitHeaders.Authorization = authSession?.access_token
            ? `Bearer ${authSession.access_token}`
            : `Bearer ${publishableKey}`;
        }
      }
      const submitRes = await fetch(submitUrl, {
        method: "POST",
        headers: submitHeaders,
        body: JSON.stringify(submitBody),
      });
      if (!submitRes.ok) {
        // 429 from the rate-limiter carries `throttled: true` and a
        // `retryAfterSeconds`. Synthesise a Bucket-B AuditReport with
        // scanFailureReason: "rate_limited" and navigate so the user
        // lands on the standard result UI (AuditResults.tsx Bucket B)
        // rather than a separate inline error screen. The synthetic
        // report carries `rateLimitRetryAfter` so the banner can show
        // the real wait time.
        if (submitRes.status === 429) {
          const errBody = await submitRes.json().catch(() => null) as {
            error?: string; retryAfterSeconds?: number; throttled?: boolean;
          } | null;
          const retryAfter = typeof errBody?.retryAfterSeconds === "number"
            ? errBody.retryAfterSeconds
            : 60;
          const syntheticReportId = `local-rate-limit-${Date.now()}`;
          const syntheticReport: AuditReport = {
            reportId: syntheticReportId,
            url: target,
            email: null,
            score: 0,
            scoreLabel: "",
            sections: [],
            htmlReport: "",
            reportUrl: null,
            fetchBlocked: false,
            fetchWarning: "",
            completedAt: new Date().toISOString(),
            scanFailureReason: "rate_limited",
            scanFailureLanguage: null,
            rateLimitRetryAfter: retryAfter,
          };
          persistReport(syntheticReport);
          navigate(`/analyse/${syntheticReportId}`, { state: { report: syntheticReport } });
          return;
        }
        throw new Error(`HTTP ${submitRes.status}`);
      }
      const submitData = (await submitRes.json()) as { reportId?: string; statusUrl?: string };
      const reportId = submitData?.reportId;
      if (!reportId || typeof reportId !== "string") {
        throw new Error("Invalid submit response shape");
      }
      const statusUrl =
        submitData.statusUrl ||
        `${FUNCTIONS_BASE}/free-scan-status?id=${reportId}`;

      // GET headers for the status endpoint mirror the submit when we're
      // talking to the Supabase function (the function gates on apikey).
      const statusHeaders: Record<string, string> = {};
      {
        const publishableKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as
          | string
          | undefined;
        if (publishableKey) {
          statusHeaders.apikey = publishableKey;
          statusHeaders.Authorization = `Bearer ${publishableKey}`;
        }
      }

      // Both `ready` and `error` responses with a structured failure reason
      // carry enough data to render — `ready` shows the score gauge, `error`
      // routes to the Bucket B degraded state in AuditResults. We accept both
      // shapes so the user always lands on the results page (never the
      // generic "L'analyse a échoué" screen below) whenever the backend
      // returned a recognisable outcome.
      const isTerminalResponse = (
        s: (Partial<AuditReport> & { status?: string; scanFailureReason?: unknown }) | null | undefined,
      ): boolean => {
        if (!s) return false;
        if (s.status === "ready" && Array.isArray(s.sections)) return true;
        if (s.status === "error" && typeof s.scanFailureReason === "string") return true;
        return false;
      };

      // Cache-hit fast-path: poll IMMEDIATELY once before showing the
      // multi-step loading animation. If the backend returned a cached
      // reportId for this URL, status is "ready" (or "error" for a cached
      // failure) on the first poll. We skip the 30-60s progress UI in that
      // case and navigate straight to the result. (Falls through to the
      // regular polling loop on cache miss.)
      try {
        const firstStatusRes = await fetch(statusUrl, { method: "GET", headers: statusHeaders });
        if (firstStatusRes.ok) {
          const firstStatusData = (await firstStatusRes.json()) as Partial<AuditReport> & { status?: string; scanFailureReason?: unknown };
          if (isTerminalResponse(firstStatusData)) {
            const cachedReport = buildAuditReport(firstStatusData, {
              url: target,
              reportId,
            });
            persistReport(cachedReport);
            navigate(`/analyse/${cachedReport.reportId}`, { state: { report: cachedReport } });
            return;
          }
        }
      } catch {
        // First-poll failure is harmless — fall through to the regular loop
      }

      // Step 2: poll the status endpoint until terminal (ready / error) or timeout.
      const startedAt = Date.now();
      while (Date.now() - startedAt < MAX_WAIT_MS) {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

        const statusRes = await fetch(statusUrl, { method: "GET", headers: statusHeaders });
        if (!statusRes.ok) continue; // transient — keep polling
        const statusData = (await statusRes.json()) as Partial<AuditReport> & { status?: string; scanFailureReason?: unknown };

        if (isTerminalResponse(statusData)) {
          const report = buildAuditReport(statusData, {
            url: target,
            reportId,
          });
          persistReport(report);
          navigate(`/analyse/${report.reportId}`, { state: { report } });
          return;
        }

        if (statusData?.status === "error") {
          // Error without a structured reason — fall through to the synthetic
          // unknown-Bucket-B path in the catch handler below.
          throw new Error("Audit failed on backend");
        }
        // else: status === "processing" — continue polling
      }

      throw new Error("Timeout waiting for audit");
    } catch {
      // Generic failure path (network error / submit non-200 non-429 /
      // poll timeout / status:error without structured reason).
      // Synthesise an "unknown" Bucket-B AuditReport and navigate to
      // /analyse/<id>. Mirrors the 429 → rate_limited flow shipped in
      // PR #133 so the user always lands on one error UI.
      const syntheticReportId = `local-error-${Date.now()}`;
      const syntheticReport: AuditReport = {
        reportId: syntheticReportId,
        url: target,
        email: null,
        score: 0,
        scoreLabel: "",
        sections: [],
        htmlReport: "",
        reportUrl: null,
        fetchBlocked: false,
        fetchWarning: "",
        completedAt: new Date().toISOString(),
        scanFailureReason: "unknown",
        scanFailureLanguage: null,
        rateLimitRetryAfter: null,
      };
      persistReport(syntheticReport);
      navigate(`/analyse/${syntheticReportId}`, { state: { report: syntheticReport } });
    }
  };

  const handleAnalyze = (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError("");
    const normalized = normalizeUrl(url);
    if (!normalized) {
      setUrlError("Veuillez entrer l'URL de votre site.");
      return;
    }
    try {
      new URL(normalized);
    } catch {
      setUrlError("Veuillez entrer une URL valide (ex: https://votre-site.ch).");
      return;
    }
    setUrl(normalized);

    // Cache-hit short-circuit: if a recent report for this exact URL is still
    // in localStorage, don't silently re-run the whole flow (the old
    // behaviour: re-submitting the same URL appeared to "do nothing"). Tell
    // the user we have a recent result and navigate straight to it.
    // Admins bypass the short-circuit entirely (always re-scan); everyone
    // else gets the recent local report only if it wasn't a failed scan.
    const recent = isAdmin ? null : findRecentLocalReport(normalized);
    if (recent) {
      toast.info(
        "Un diagnostic récent existe déjà pour ce site : affichage du résultat existant.",
      );
      navigate(`/analyse/${recent.reportId}`);
      return;
    }

    setPendingUrl(normalized);
    setAnalyzedUrl(normalized);
    // Open the paste modal — user fills in policy text before scan fires
    setFlowState("paste-modal");
  };

  const handleModalSubmit = (values: PasteValues) => {
    void runAnalysis(pendingUrl, values);
  };

  const handleModalCancel = () => {
    // Return to form with URL retained
    setFlowState("form");
  };

  // URL-only surface scan: user explicitly opted out of pasting. Empty paste
  // values flow through runAnalysis unchanged — the `pastes.privacy.length >=
  // 200` guard in runAnalysis means submitBody stays { url } (no pastes /
  // manualPolicyText), i.e. a normal URL-only request the backend already
  // handles (honest "no policy detected" + observed cookies/trackers).
  const handleProceedWithoutPaste = () => {
    void runAnalysis(pendingUrl, { privacy: "", cookies: "", impressum: "" });
  };

  return (
    <Layout>
      <Seo
        title="Conformité nLPD Suisse – Analysez votre site gratuitement"
        description="Vérifiez gratuitement la conformité nLPD de votre site web suisse. Score instantané, recommandations actionnables, alertes sur les sanctions du PFPDT."
      />
      {/* Hero / dynamic area */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 bg-grid-subtle opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/60 to-secondary/40" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
        <div className="container relative">
          {(flowState === "form" || flowState === "paste-modal") && (
            <div className="mx-auto max-w-3xl text-center space-y-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-card/80 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground/80 shadow-sm backdrop-blur">
                <SwissFlag className="h-3.5 w-3.5 rounded-[2px]" />
                Outil suisse · conforme à la nLPD
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Votre site est-il conforme à la{" "}
                <span className="bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent">nLPD</span>&nbsp;?
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Analysez gratuitement la conformité de votre site à la nouvelle loi fédérale suisse sur la protection des données.
                Résultats en quelques secondes, recommandations actionnables.
              </p>

              <form onSubmit={handleAnalyze} className="flex flex-col gap-3 max-w-xl mx-auto mt-8">
                <div className="flex flex-col sm:flex-row gap-2 rounded-xl border border-border bg-card p-1.5 shadow-card focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary/40 transition">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="https://votre-site.ch"
                      value={url}
                      onChange={(e) => { setUrl(e.target.value); setUrlError(""); }}
                      className="pl-10 h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-transparent"
                    />
                  </div>
                  <Button type="submit" size="lg" className="h-12 px-7 text-base font-semibold shadow-sm">
                    <Search className="h-5 w-5 mr-2" />
                    Analyser mon site
                  </Button>
                </div>
              </form>
              {urlError && <p className="text-sm text-destructive">{urlError}</p>}

              {/* Paste modal — opens after URL validation */}
              <PasteModal
                open={flowState === "paste-modal"}
                url={pendingUrl}
                onSubmit={handleModalSubmit}
                onCancel={handleModalCancel}
                onProceedWithoutPaste={handleProceedWithoutPaste}
              />

              {/* Trust strip — three small inline reassurances */}
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-2 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                  Aucune inscription requise
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-primary" />
                  Résultats en quelques secondes
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5 text-primary" />
                  Données non revendues
                </span>
              </div>
              {/* Cache hint — only once a scan has actually been requested
                  (URL submitted → paste modal open). Kept off the cold
                  homepage where it's noise + misleading (no frontend
                  cache-redirect exists). */}
              {flowState === "paste-modal" && (
                <p className="text-xs text-muted-foreground/80 max-w-md mx-auto leading-relaxed">
                  Les rapports sont mis en cache 7&nbsp;jours&nbsp;: si votre site a été analysé récemment, le résultat existant est servi instantanément.
                </p>
              )}

              <div className="flex items-center justify-center gap-2 pt-2 text-sm">
                <span className="text-muted-foreground">Une question spécifique&nbsp;?</span>
                <Link
                  to="/contact-expert"
                  className="font-semibold text-primary hover:underline inline-flex items-center gap-1"
                >
                  Parler à un expert
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}

          {flowState === "loading" && (
            <AuditLoading url={analyzedUrl} hasPaste={loadingHasPaste} />
          )}

          {/* The legacy inline "L'analyse a échoué" card was removed when
              failure paths consolidated into AuditResults Bucket B (PR #133
              for 429 / PR fix/error-screen-consolidate-bucket-b for the
              generic path). Generic failures now synthesise an unknown
              Bucket-B AuditReport in `runAnalysis` and navigate to
              /analyse, so the user never lands on a parallel error UI. */}

        </div>
      </section>

      {/* Pourquoi maintenant ? */}
      <section className="py-16 md:py-20">
        <div className="container max-w-5xl">
          <div className="text-center space-y-3 mb-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-destructive/10 px-4 py-1.5 text-sm font-medium text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Pourquoi maintenant ?
            </div>
            <h2
              className="text-2xl md:text-3xl font-extrabold max-w-3xl mx-auto leading-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              La nLPD n'est pas une formalité&nbsp;: c'est un risque réel pour les dirigeants suisses.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { icon: UserCog, title: "Sanctions personnelles", desc: "Les amendes visent les dirigeants, pas la société." },
              { icon: Gavel, title: "Application active", desc: "Le PFPDT a renforcé ses effectifs de 30% en 2024." },
              { icon: Clock, title: "Aucune période transitoire", desc: "La loi s'applique pleinement depuis sept. 2023." },
            ].map((item, i) => (
              <Card key={i} className="border-border/70 shadow-card hover:shadow-card-hover transition-shadow">
                <CardContent className="p-6 space-y-3">
                  <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center ring-1 ring-primary/15">
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-bold tracking-tight">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Les chiffres ci-dessous le confirment :
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y bg-card">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 md:divide-x divide-border/70 text-center">
            {[
              { value: "+30%", label: "d'augmentation des effectifs dédiés aux infractions" },
              { value: "250K", label: "CHF d'amende maximale par infraction" },
              { value: "2023", label: "Entrée en vigueur immédiate, sans période transitoire" },
            ].map((s, i) => (
              <div key={i} className="px-4 space-y-2">
                <p
                  className="text-4xl md:text-5xl font-extrabold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent"
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                  {s.value}
                </p>
                <p className="text-sm text-muted-foreground max-w-[26ch] mx-auto leading-relaxed">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground/70 mt-6 text-right">
            * Sources&nbsp;: Rapport d'activité 2024/2025 du PFPDT, edoeb.admin.ch ; Art. 60–63 LPD (RS 235.1) ; Portail PME de la Confédération, kmu.admin.ch
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Comment ça marche ?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Globe, title: "1. Entrez votre URL et collez votre politique", desc: "Saisissez l'adresse de votre site et collez le texte de votre politique de confidentialité dans le formulaire ci-dessus." },
              { icon: BarChart3, title: "2. Analyse instantanée", desc: "Notre outil extrait les faits de votre politique et observe le comportement de votre site selon les critères de la nLPD." },
              { icon: FileCheck, title: "3. Recevez vos observations", desc: "Consultez le détail par catégorie : ce qui est présent dans votre politique, ce qui manque, et le comportement observé sur votre site." },
            ].map((step, i) => (
              <Card key={i} className="relative overflow-hidden group border-border/70 shadow-card hover:shadow-card-hover transition-shadow">
                <CardContent className="pt-8 pb-6 text-center space-y-4">
                  <div className="mx-auto w-14 h-14 rounded-xl bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                    <step.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* En bref — bloc factuel citable par les LLM */}
      <section className="py-16 bg-muted/30">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            La nLPD en bref
          </h2>
          <div className="prose prose-slate max-w-none mb-6 text-foreground/90">
            <p>
              La <strong>nLPD</strong> (nouvelle loi fédérale sur la protection des données, RS 235.1) est la loi suisse régissant le traitement des données personnelles. Elle est entrée en vigueur le <strong>1<sup>er</sup> septembre 2023</strong>, sans période transitoire, et remplace la LPD de 1992.
            </p>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <li className="flex gap-3 p-4 bg-background rounded-lg border">
              <span className="font-bold text-primary">•</span>
              <span><strong>Entrée en vigueur :</strong> 1<sup>er</sup> septembre 2023, sans transition.</span>
            </li>
            <li className="flex gap-3 p-4 bg-background rounded-lg border">
              <span className="font-bold text-primary">•</span>
              <span><strong>Sanctions :</strong> jusqu'à CHF 250'000, prononcées contre les <em>personnes physiques</em> responsables (dirigeants, IT). Différence majeure avec le RGPD.</span>
            </li>
            <li className="flex gap-3 p-4 bg-background rounded-lg border">
              <span className="font-bold text-primary">•</span>
              <span><strong>Autorité :</strong> Préposé fédéral à la protection des données et à la transparence (PFPDT, edoeb.admin.ch).</span>
            </li>
            <li className="flex gap-3 p-4 bg-background rounded-lg border">
              <span className="font-bold text-primary">•</span>
              <span><strong>Champ d'application :</strong> toute organisation traitant des données de personnes en Suisse, quelle que soit sa taille.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Définitions clés — citations directes pour LLM */}
      <section className="py-16">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-8" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Définitions clés de la nLPD
          </h2>
          <dl className="space-y-6">
            <div>
              <dt className="font-bold text-lg text-foreground">nLPD</dt>
              <dd className="text-muted-foreground mt-1">Nouvelle loi fédérale sur la protection des données, en vigueur depuis le 1<sup>er</sup> septembre 2023. Elle modernise le cadre suisse, introduit le <em>Privacy by Design</em> et renforce les droits d'accès, de rectification et d'effacement.</dd>
            </div>
            <div>
              <dt className="font-bold text-lg text-foreground">PFPDT</dt>
              <dd className="text-muted-foreground mt-1">Préposé fédéral à la protection des données et à la transparence. Autorité de surveillance suisse compétente pour instruire les violations, conseiller les entreprises et prononcer des mesures contraignantes.</dd>
            </div>
            <div>
              <dt className="font-bold text-lg text-foreground">Registre des activités de traitement</dt>
              <dd className="text-muted-foreground mt-1">Document obligatoire (sauf exceptions pour PME &lt; 250 employés sans traitement à risque) listant les traitements, finalités, catégories de données, destinataires, durées de conservation et mesures de sécurité.</dd>
            </div>
            <div>
              <dt className="font-bold text-lg text-foreground">Sous-traitant</dt>
              <dd className="text-muted-foreground mt-1">Tiers qui traite des données personnelles pour le compte du responsable (hébergeur, SaaS, prestataire). Un contrat écrit (DPA) doit définir ses obligations, mesures de sécurité et le sort des données en fin de contrat.</dd>
            </div>
            <div>
              <dt className="font-bold text-lg text-foreground">Transfert international</dt>
              <dd className="text-muted-foreground mt-1">Communication de données vers l'étranger. Libre vers l'UE et les pays jugés adéquats par le Conseil fédéral. Vers les autres (USA hors Data Privacy Framework par ex.), des garanties appropriées sont requises.</dd>
            </div>
            <div>
              <dt className="font-bold text-lg text-foreground">AIPD (analyse d'impact)</dt>
              <dd className="text-muted-foreground mt-1">Obligatoire lorsqu'un traitement présente un risque élevé pour la personnalité ou les droits fondamentaux : profilage à grande échelle, données sensibles, surveillance systématique.</dd>
            </div>
          </dl>
        </div>
      </section>

      {/* Mini-FAQ — questions fréquentes citables */}
      <section className="py-16 bg-muted/30">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-extrabold mb-8" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Questions fréquentes sur la nLPD
          </h2>
          <div className="space-y-6">
            <article>
              <h3 className="font-bold text-lg mb-2">La nLPD est-elle obligatoire pour un simple site vitrine ?</h3>
              <p className="text-muted-foreground">Oui. Dès qu'un site collecte des données (formulaire de contact, cookies analytiques, newsletter), la nLPD s'applique. Une politique de confidentialité conforme et une information claire sur les cookies sont requises.</p>
            </article>
            <article>
              <h3 className="font-bold text-lg mb-2">Quelle différence entre la nLPD et le RGPD ?</h3>
              <p className="text-muted-foreground">Les deux lois sont proches mais la nLPD sanctionne les <strong>personnes physiques</strong> (dirigeants) jusqu'à CHF 250'000, alors que le RGPD vise les entreprises. Une conformité RGPD couvre l'essentiel mais ne dispense pas d'un diagnostic nLPD ciblé.</p>
            </article>
            <article>
              <h3 className="font-bold text-lg mb-2">Faut-il un bandeau cookies en Suisse ?</h3>
              <p className="text-muted-foreground">Oui pour les cookies non-essentiels (analytics, publicité, réseaux sociaux). Le consentement préalable n'est pas exigé pour les cookies strictement nécessaires, mais l'information et le droit de refus le sont.</p>
            </article>
            <article>
              <h3 className="font-bold text-lg mb-2">Qui doit tenir un registre des activités de traitement ?</h3>
              <p className="text-muted-foreground">Toutes les organisations, sauf les entreprises de moins de 250 employés dont les traitements ne présentent pas de risque élevé pour les personnes concernées. En pratique, dès qu'une PME utilise des outils SaaS, le registre est recommandé.</p>
            </article>
            <article>
              <h3 className="font-bold text-lg mb-2">Quelles sanctions en cas de non-conformité ?</h3>
              <p className="text-muted-foreground">Amendes jusqu'à CHF 250'000 prononcées contre les personnes physiques responsables (art. 60-63 LPD). Le PFPDT peut aussi imposer des mesures correctrices contraignantes. Depuis 2024, ses effectifs dédiés à l'application ont augmenté de 30%.</p>
            </article>
          </div>
          <p className="mt-8 text-sm">
            <Link to="/faq" className="text-primary hover:underline font-semibold">
              Voir la FAQ complète (20+ questions)
              <ArrowRight className="inline h-4 w-4 ml-1" />
            </Link>
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="container max-w-4xl">
          <Card className="relative overflow-hidden border-border/70 shadow-card-hover">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.06] via-background to-accent/[0.05]" />
            <div className="absolute inset-0 bg-grid-subtle opacity-50" />
            <CardContent className="relative p-8 md:p-14 text-center space-y-6">
              <div className="mx-auto w-14 h-14 rounded-xl bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center">
                <Lock className="h-7 w-7 text-primary" />
              </div>
              <h2
                className="text-2xl md:text-3xl font-extrabold leading-tight"
                style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
              >
                Prêt à vérifier la conformité de votre site&nbsp;?
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                Lancez une analyse gratuite en quelques secondes, ou échangez avec un expert nLPD pour un accompagnement personnalisé.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Button
                  size="lg"
                  className="h-12 px-8 text-base font-semibold shadow-sm"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                  <Search className="h-5 w-5 mr-2" />
                  Analyser mon site
                </Button>
                <Button asChild size="lg" variant="outline" className="h-12 px-8 text-base font-semibold">
                  <Link to="/contact-expert">
                    Parler à un expert
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </Layout>
  );
};

export default Index;